//https://medium.com/@schlunzk/integrating-google-maps-api-in-react-redux-part-1-6b036014f4a6
import getDistance from "geolib/es/getDistance";
import { Map, AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import {
  fetchLocation,
  updateLocation,
  updateMapCenter,
  updateRadius,
  setLocationDisplay,
} from "../../actions/currentLocation";
import {
  fetchResults,
  startAdHocSearch,
  stopAdHocSearch,
  updateSelectedCuisines,
  selectRestaurantFromMap,
} from "../../actions/restaurants";
import { toggleList, toggleMap } from "../../actions/results";
import { LIST_VIEW, MAP_VIEW } from "../../actions/types";
import { getLatLong } from "../../util/geoUtils";
import {
  filterByLeafRating,
  filterBySelectedCuisines,
} from "../../util/restaurantFilters";
import { clusterRestaurants, getZoomToSpreadCluster } from "../../util/clustering";
import { backendLog } from "../../util/logging";
import MapMarker from "./MapMarker";
import ClusterMarker from "./ClusterMarker";
import BottomSheet, { SHEET_STATES } from "./mobile/BottomSheet";
import CardCarousel from "./mobile/CardCarousel";
import CompactList from "./mobile/CompactList";
import ExpandedCard from "./mobile/ExpandedCard";
import useEvent from "../../hooks/useEvent";
import useCity from "../../hooks/useCity";
import { useSidebar } from "../../contexts/SidebarContext";
import EventMapOverlay from "./EventMapOverlay";
import SearchBar from "./SearchBar";
import UnifiedSearchBar from "./UnifiedSearchBar";
import ActiveFilters from "./ActiveFilters";
import CreateButton from "./CreateButton";
import LocationPermissionModal from "./LocationPermissionModal";
import FollowCityModal from "./FollowCityModal";
import SaveSearchModal from "./SaveSearchModal";
import { reverseGeocode } from "../../util/geoUtils";
import {
  StickyDiv,
  RightContent,
  ClickableIconNoBorder,
  GrayedOutModal,
  ModalContent,
} from "../../styles/styled-components-utils";

// Re-export for backward compatibility with other files that import from MapResults
export { StickyDiv, GrayedOutModal, ModalContent };

const MapResults = ({
  currLocation,
  google,
  restaurants,
  selected,
  updateLocation,
  fetchResultsAction,
  updateRadius,
  updateMapCenter,
  mapCenter,
  fetchLocation,
  selectedCuisines,
  toggleList,
  toggleMap,
  view,
  adHocSearchActive,
  adHocSearchJobId,
  adHocRestaurantsProcessed,
  adHocTotalRestaurants,
  adHocVeganResultsFound,
  startAdHocSearchAction,
  stopAdHocSearchAction,
  updateSelectedCuisinesAction,
  selectRestaurantFromMapAction,
  minLeafCount,
  verificationTier,
  setLocationDisplayAction,
  token,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [initialize, setInitialize] = useState(false);
  const [zoom, setZoom] = useState(12);
  // Visual center for GoogleMapReact, decoupled from the mapCenter feedback loop.
  // mapCenter is updated on every GoogleMapReact onChange (drag, zoom, pan) via handleChange,
  // so using it as the center prop creates a loop where stale onChange events from a
  // previous position can overwrite a programmatic move. Instead, we track the intended
  // visual center in local state: updated by programmatic moves (updateLocation) and
  // explicit UI interactions (cluster click, restaurant select), but NOT by handleChange.
  const [googleMapCenter, setGoogleMapCenter] = useState(
    currLocation.lat ? { lat: currLocation.lat, lng: currLocation.lng } : null
  );
  // Sync from currLocation when it changes (searches, geolocation, list navigation)
  useEffect(function syncMapCenterFromLocation() {
    if (currLocation.lat && currLocation.lng) {
      setGoogleMapCenter({ lat: currLocation.lat, lng: currLocation.lng });
    }
  }, [currLocation.lat, currLocation.lng]);
  const toggleResults = () => (view === MAP_VIEW ? toggleList() : toggleMap());
  // setSelfState({ ...selfState, isOpen: !isOpen });
  // const [scrolled, setScrolled] = useState(false);
  const [bounds, setBounds] = useState({ se: {}, nw: {}, sw: {}, ne: {} });
  const [showLabel, setShowLabel] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [sheetState, setSheetState] = useState(SHEET_STATES.COLLAPSED);
  // Track if we're showing expanded card (vs list) in half/full states
  const [showExpandedCard, setShowExpandedCard] = useState(false);
  const mapInstance = useMap();
  const [mapsApi, setMapsApi] = useState(null);

  const [showFollowModal, setShowFollowModal] = useState(false);
  const [followCityInfo, setFollowCityInfo] = useState(null);

  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveModalInfo, setSaveModalInfo] = useState(null);

  const handleFollowCityClick = useCallback(async () => {
    if (!mapCenter?.lat || !mapCenter?.lng) return;
    const geo = await reverseGeocode(mapCenter.lat, mapCenter.lng);
    setFollowCityInfo({
      cityName: geo.city || "this area",
      state: geo.state || "",
      lat: mapCenter.lat,
      lng: mapCenter.lng,
    });
    setShowFollowModal(true);
  }, [mapCenter]);

  const handleSaveSearchClick = useCallback(async () => {
    if (!mapCenter?.lat || !mapCenter?.lng) return;
    const geo = await reverseGeocode(mapCenter.lat, mapCenter.lng);
    const urlParams = new URLSearchParams(location.search);
    const cuisineParam = urlParams.get("cuisine");
    setSaveModalInfo({
      cityName: geo.city || "this area",
      state: geo.state || "",
      lat: mapCenter.lat,
      lng: mapCenter.lng,
      keywords: urlParams.get("keywords") || "",
      cuisine: cuisineParam ? cuisineParam.split(",").filter(Boolean) : [],
      radius: currLocation.radius,
      zoom,
    });
    setShowSaveModal(true);
  }, [mapCenter, location.search, currLocation.radius, zoom]);

  // Mobile detection
  const isMobile = typeof window !== "undefined" && window.innerWidth < 600;

  // Get event context if on event route
  const { event, loading: eventLoading } = useEvent();
  const useFollowFlow = !!event || !token;

  // Get city context if on city route
  const { city, state, normalizedLocation, loading: cityLoading } = useCity();

  // Get sidebar context for collapsible sidebar
  const { setIsSidebarOpen, isCollapsibleMode } = useSidebar();
  const hasLatLong = () => {
    const query = location.search;
    return query && new URLSearchParams(query).get("lat");
  };

  const hasZoom = () => {
    const query = location.search;
    return query && new URLSearchParams(query).get("zoom");
  };

  // Request browser geolocation. Returns a cancel function for effect cleanup.
  const requestLocationPermission = () => {
    let cancelled = false;
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (cancelled) return;
          const { latitude, longitude } = position.coords;
          updateLocation({ lat: latitude, lng: longitude });
          setLocationDisplayAction("gps", null);
        },
        (error) => {
          if (cancelled) return;
          console.log("error getting position");
          console.log(error);
          fetchLocation(false);
        },
        { timeout: 10000, enableHighAccuracy: false }
      );
    }
    return () => { cancelled = true; };
  };

  // Handle location modal actions
  const handleLocationAllow = () => {
    setShowLocationModal(false);
    localStorage.setItem("vegifyit_location_modal_shown", "true");
    requestLocationPermission();
  };

  const handleLocationSkip = () => {
    setShowLocationModal(false);
    localStorage.setItem("vegifyit_location_modal_shown", "true");
    fetchLocation(false);
  };

  useEffect(function redirectLegacyLocationCodes() {
    const locationMap = {
      1018127: "massachusetts/boston",
      1021916: "new_hampshire/nashua",
      1021903: "new_hampshire/manchester",
      1021832: "new_hampshire/concord",
    };
    if (location.search) {
      const searchUrl = new URLSearchParams(location.search);
      const loc = searchUrl.get("loc");
      if (loc && locationMap[loc]) {
        navigate("cities/" + locationMap[loc]);
      } else if (loc && !locationMap[loc]) {
        backendLog("Unrecognized loc: " + loc);
      }
    }
  }, [location.search, navigate]);
  // Track whether we've had a location set (to distinguish initial load from nav back to "/")
  const hasInitialized = useRef(false);

  useEffect(function resolveInitialLocation() {
    let cancelGeo;
    async function getLocation() {
      // Priority 1: If we have event data, use it (event data includes lat/lng)
      if (event && event.lat && event.lng) {
        updateLocation({ lat: event.lat, lng: event.lng });
        if (event.zoom) {
          setZoom(event.zoom);
        }
        return;
      }

      // Priority 2: If we have city data, geocode it
      if (normalizedLocation) {
        const { lat, lng } = (await getLatLong(normalizedLocation)).data;
        return updateLocation({ lat, lng });
      }
    }
    if (hasZoom()) {
      const searchUrl = new URLSearchParams(location.search);
      setZoom(parseFloat(searchUrl.get("zoom")));
    }
    if (hasLatLong()) {
      hasInitialized.current = true;
      const searchUrl = new URLSearchParams(location.search);
      const lat = parseFloat(searchUrl.get("lat"));
      const lng = parseFloat(searchUrl.get("lng"));
      updateLocation({
        lat,
        lng,
      });
    } else if (location.pathname === "/" && hasInitialized.current) {
      // Navigated back to "/" after already having a location — re-request geolocation
      cancelGeo = requestLocationPermission();
    } else if (location.pathname !== "/") {
      getLocation();
    }
    return () => { if (cancelGeo) cancelGeo(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, normalizedLocation, location.search]);
  useEffect(function showLocationModalOnMount() {
    let cancelGeo;
    // Check if we should show the location modal
    if (
      navigator.geolocation &&
      !hasLatLong() &&
      location.pathname === "/"
    ) {
      const modalShown = localStorage.getItem("vegifyit_location_modal_shown");
      if (!modalShown) {
        // Show modal first
        setShowLocationModal(true);
      } else {
        // Modal was already shown, request location directly
        cancelGeo = requestLocationPermission();
      }
    }
    return () => { if (cancelGeo) cancelGeo(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // useEffect(() => {
  //   window.onscroll = () =>
  //     window.pageYOffset >= 100 ? setScrolled(true) : setScrolled(false);

  //   return () => (window.onscroll = null);
  // }, []);
  useEffect(function updateLabelVisibilityOnBoundsChange() {
    const isInRect = (lat, lng) => {
      return (
        lat > bounds.se.lat &&
        lat < bounds.nw.lat &&
        lng < bounds.se.lng &&
        lng > bounds.nw.lng
      );
    };
    // Apply same filters as map markers for consistent label behavior
    // Note: keyword filtering is handled by backend, not FE
    const filteredRestaurants = restaurants
      .filter((r) => filterBySelectedCuisines(r, selectedCuisines))
      .filter((r) => filterByLeafRating(r, minLeafCount, verificationTier));
    setShowLabel(
      filteredRestaurants.filter((rest) =>
        isInRect(rest.geoJson.coordinates[1], rest.geoJson.coordinates[0])
      ).length <= 5
    );
  }, [bounds, restaurants, updateRadius, selectedCuisines, minLeafCount, verificationTier]);
  const handleChange = (input) => {
    const { center, bounds } = input;
    setBounds(bounds);
    // Calculate radius based on map bounds (works for both initial load and subsequent changes)
    if (
      mapCenter.lat &&
      !currLocation.fetchLocation &&
      center &&
      bounds &&
      bounds.nw
    ) {
      const distance = getDistance(center, bounds.nw) / 1000;
      updateRadius(distance * 2);
      updateMapCenter(center);
    }
    setInitialize(true);
  };

  // Filtered restaurants (memoized)
  // Note: keyword filtering is handled by backend, not FE
  const filteredRestaurants = useMemo(() => {
    return restaurants
      .filter((r) => filterBySelectedCuisines(r, selectedCuisines))
      .filter((r) => filterByLeafRating(r, minLeafCount, verificationTier))
      .sort((a, b) => {
        const distA = getDistance(currLocation, a.geoJson.coordinates);
        const distB = getDistance(currLocation, b.geoJson.coordinates);
        return distA - distB;
      });
  }, [restaurants, selectedCuisines, minLeafCount, verificationTier, currLocation]);

  // Cluster restaurants based on zoom level
  const { clusters, singles } = useMemo(() => {
    return clusterRestaurants(filteredRestaurants, zoom, bounds);
  }, [filteredRestaurants, zoom, bounds]);

  // Handle cluster click - zoom in to spread cluster
  const handleClusterClick = useCallback((cluster) => {
    if (mapInstance) {
      const targetZoom = getZoomToSpreadCluster(cluster, zoom);
      mapInstance.setZoom(targetZoom);
      mapInstance.panTo(cluster.center);
      setZoom(targetZoom);
      setGoogleMapCenter(cluster.center);
      updateMapCenter(cluster.center);
    }
  }, [mapInstance, zoom, updateMapCenter]);

  // Handle restaurant selection from bottom sheet
  const handleRestaurantSelect = useCallback((restaurantId, restaurant) => {
    selectRestaurantFromMapAction(restaurantId);
    if (restaurant) {
      const coords = {
        lat: restaurant.geoJson.coordinates[1],
        lng: restaurant.geoJson.coordinates[0],
      };
      setGoogleMapCenter(coords);
      updateMapCenter(coords);
    }
  }, [selectRestaurantFromMapAction, updateMapCenter]);

  // Handle carousel scroll end - pan map to restaurant
  const handleCarouselScrollEnd = useCallback((restaurant) => {
    if (restaurant) {
      const coords = {
        lat: restaurant.geoJson.coordinates[1],
        lng: restaurant.geoJson.coordinates[0],
      };
      setGoogleMapCenter(coords);
      updateMapCenter(coords);
    }
  }, [updateMapCenter]);

  // Handle bottom sheet state change
  const handleSheetStateChange = useCallback((newState) => {
    setSheetState(newState);
  }, []);

  // Collapse bottom sheet (used by "See on map" button)
  const collapseSheet = useCallback(() => {
    setSheetState(SHEET_STATES.COLLAPSED);
    setShowExpandedCard(false);
  }, []);

  // Expand to half view when carousel card is tapped (shows expanded card)
  const expandFromCarousel = useCallback(() => {
    setShowExpandedCard(true);
    setSheetState(SHEET_STATES.HALF);
  }, []);

  // Go back to list from expanded card (stay in current sheet state)
  const backToList = useCallback(() => {
    setShowExpandedCard(false);
  }, []);

  // Handle ad hoc search
  const handleAdHocSearch = useCallback(async () => {
    try {
      const lat = mapCenter.lat;
      const lng = mapCenter.lng;
      const radius = currLocation.radius;

      if (!lat || !lng) {
        alert("Please enable location services or search for a location first");
        return;
      }

      if (adHocSearchActive) {
        return;
      }

      await startAdHocSearchAction(lat, lng, radius);
    } catch (error) {
      console.error("Error starting ad hoc search:", error);
      alert(error.response?.data?.error || "Failed to start discovery search");
    }
  }, [mapCenter, currLocation.radius, adHocSearchActive, startAdHocSearchAction]);

  // Initialize Maps API from map instance
  useEffect(function initMapsApi() {
    if (mapInstance && window.google?.maps && !mapsApi) {
      setMapsApi(window.google.maps);
    }
  }, [mapInstance, mapsApi]);

  // Programmatic re-centering: pan to googleMapCenter when it changes
  const prevCenterRef = useRef(null);
  useEffect(function panToNewCenter() {
    if (!mapInstance || !googleMapCenter) return;
    // Skip if center hasn't actually changed (avoid panning on every render)
    const prev = prevCenterRef.current;
    if (prev && prev.lat === googleMapCenter.lat && prev.lng === googleMapCenter.lng) return;
    prevCenterRef.current = googleMapCenter;
    mapInstance.panTo(googleMapCenter);
  }, [mapInstance, googleMapCenter]);

  // Sync event heading to map when event or map changes
  useEffect(function syncEventHeading() {
    if (!mapInstance || event?.heading === undefined) return;
    try { mapInstance.setHeading(event.heading || 0); } catch (e) {}
  }, [mapInstance, event?.heading]);

  // Dynamic page title for homepage searches
  const homeUrlParams = new URLSearchParams(location.search);
  const searchQuery = homeUrlParams.get("keywords");
  const homeKeywords = searchQuery;
  const homeHasSearch = homeUrlParams.get("lat") && homeUrlParams.get("lng");
  const homeTitle = homeHasSearch
    ? homeKeywords
      ? `Vegan ${homeKeywords} near you | VegifyIt`
      : "Vegan options near you | VegifyIt"
    : "Find Vegan Options Near You | VegifyIt - Discover Plant-Based Dining";
  const homeDescription = homeHasSearch
    ? homeKeywords
      ? `Find vegan ${homeKeywords} at restaurants near you with VegifyIt. Discover plant-based options at vegan and non-vegan restaurants.`
      : "VegifyIt helps you find vegan options at restaurants near you with an interactive map. Explore plant-based dining effortlessly."
    : "VegifyIt helps you find vegan options at restaurants, food trucks, and vendors near you with an interactive map. Explore plant-based dining effortlessly, whether you're looking for vegan restaurants or vegan choices at non-vegan establishments.";

  return (
    <div className="map-results-wrapper">
      {location.pathname !== "/" && !location.pathname.startsWith("/lists/") && !location.pathname.startsWith("/cities/") && !location.pathname.startsWith("/events/") && (
        <Helmet>
          <title>
            {normalizedLocation
              ? `Vegan Food Near ${normalizedLocation} | VegifyIt`
              : "VegifyIt - Discover Vegan Food Nearby"}
          </title>
          <meta
            name="description"
            content={
              normalizedLocation
                ? `Discover vegan-friendly dishes at vegan and non-vegan restaurants in ${normalizedLocation}! It can be hard to find vegan-specific restaurants, so we search across all restaurants for vegan options.`
                : "Discover vegan-friendly dishes at vegan and non-vegan restaurants near you! It can be hard to find vegan-specific restaurants, so we search across all restaurants for vegan options."
            }
          />
        </Helmet>
      )}
      {/* City, list, and event pages own their own meta tags via their page
          components so they can honor curated SEO content (e.g. cluster
          seoMetaDescription, event JSON-LD). Don't duplicate them here. */}
      {location.pathname === "/" && (
        <Helmet>
          <title>{homeTitle}</title>
          <meta name="description" content={homeDescription} />
          <meta
            name="keywords"
            content="vegan dining, vegan restaurants, find vegan food, plant-based options, vegan food map, VegifyIt, vegan food discovery, food trucks, vegan vendors"
          />
          <link rel="canonical" href="https://www.vegifyit.com" />
          <meta property="og:title" content={homeTitle} />
          <meta property="og:description" content={homeDescription} />
          <meta property="og:url" content="https://www.vegifyit.com" />
          <meta property="og:type" content="website" />
        </Helmet>
      )}
      <div
        className={`map-holder ${view === LIST_VIEW && !isCollapsibleMode && "none"}`}
        onClick={(e) => {
          // Close sidebar when map area is clicked (not markers or other interactive elements)
          // Check if click target is the map container itself
          const target = e.target;
          const isMarker = target.closest(".map-marker-offset") || target.closest(".map-marker-tag");
          const isInteractive = target.closest("button") || target.closest("a") || target.closest(".search-tools-container");
          
          if (!isMarker && !isInteractive && isCollapsibleMode) {
            setIsSidebarOpen(false);
          }
        }}
      >
        {/* Ad Hoc Search Loading Widget - pill at top center of map */}
        {/* Visible on both desktop and mobile, positioned differently via CSS */}
        {adHocSearchActive && (
          <div className="ad-hoc-map-pill">
            <div className="ad-hoc-map-pill-content">
              <i className="fas fa-spinner fa-spin ad-hoc-map-spinner"></i>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                }}
              >
                <span className="ad-hoc-map-pill-text">
                  Searching for vegan options...
                </span>
                <span
                  className="ad-hoc-map-pill-text"
                  style={{ fontSize: "0.85em", opacity: 0.8 }}
                >
                  {adHocRestaurantsProcessed} of{" "}
                  {adHocTotalRestaurants > 0 ? adHocTotalRestaurants : "?"}{" "}
                  restaurants processed
                </span>
              </div>
              <button
                type="button"
                onClick={() => stopAdHocSearchAction(adHocSearchJobId)}
                className="ad-hoc-map-cancel"
                aria-label="Cancel Discovery Search"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>
        )}
        {/* Hidden SearchBar - handles search triggering logic */}
        <div style={{ display: 'none' }}>
          <SearchBar  />
        </div>
        {/* UnifiedSearchBar for desktop - visible UI */}
        <div className="search-bar-desktop-wrapper">
          <UnifiedSearchBar />
          <ActiveFilters />
        </div>
        {!googleMapCenter && (
          <GrayedOutModal>
            <ModalContent>
              To view results on the map, enable location services or do a
              search
            </ModalContent>
          </GrayedOutModal>
        )}
        {googleMapCenter && (!event || !eventLoading) && (
          <Map
            key={event?._id || "main"}
            defaultCenter={googleMapCenter}
            defaultZoom={event?.zoom || zoom || 12}
            gestureHandling="greedy"
            fullscreenControl={false}
            mapTypeControl={false}
            streetViewControl={false}
            mapId="ae90486c215209d65677867a"
            heading={event?.heading || 0}
            onIdle={() => {
              if (!mapInstance) return;
              const center = mapInstance.getCenter();
              const b = mapInstance.getBounds();
              const newZoom = mapInstance.getZoom();
              if (!center || !b) return;
              const ne = b.getNorthEast();
              const sw = b.getSouthWest();
              const input = {
                center: { lat: center.lat(), lng: center.lng() },
                bounds: {
                  nw: { lat: ne.lat(), lng: sw.lng() },
                  se: { lat: sw.lat(), lng: ne.lng() },
                  ne: { lat: ne.lat(), lng: ne.lng() },
                  sw: { lat: sw.lat(), lng: sw.lng() },
                },
              };
              handleChange(input);
              if (newZoom !== zoom) {
                setZoom(newZoom);
              }
            }}
          >
            {clusters.map((cluster) => (
              <AdvancedMarker
                key={cluster.id}
                position={{ lat: cluster.center.lat, lng: cluster.center.lng }}
              >
                <div style={{ width: 0, height: 0, overflow: 'visible' }}>
                  <ClusterMarker
                    cluster={cluster}
                    onClusterClick={handleClusterClick}
                  />
                </div>
              </AdvancedMarker>
            ))}
            {singles.map((restaurant, index) => (
              <AdvancedMarker
                key={restaurant._id || index}
                position={{
                  lat: restaurant.geoJson.coordinates[1],
                  lng: restaurant.geoJson.coordinates[0],
                }}
                zIndex={selected === restaurant._id ? 1000 : showLabel ? 100 : 1}
              >
                <MapMarker
                  restaurant={restaurant}
                  text={restaurant.name}
                  showLabel={showLabel}
                />
              </AdvancedMarker>
            ))}
          </Map>
        )}
        <EventMapOverlay />
        <CreateButton />
      </div>
      {/* Mobile search overlay - OUTSIDE map-holder for proper z-index stacking */}
      {/* Rendered above bottom sheet (z-index: 510 vs 500) */}
      {isMobile && (
        <div className="map-overlays-container">
          <UnifiedSearchBar  />
          <ActiveFilters />
        </div>
      )}
      <LocationPermissionModal
        isOpen={showLocationModal}
        onAllow={handleLocationAllow}
        onSkip={handleLocationSkip}
      />
      {/* Mobile Bottom Sheet - only render on mobile */}
      {isMobile && (
        <BottomSheet
          restaurants={filteredRestaurants}
          selectedRestaurantId={selected}
          onRestaurantSelect={handleRestaurantSelect}
          onStateChange={handleSheetStateChange}
          state={sheetState}
          hideHeader={showExpandedCard}
          onFollowCity={useFollowFlow ? handleFollowCityClick : undefined}
          onSaveSearch={useFollowFlow ? undefined : handleSaveSearchClick}
          renderCollapsed={() => (
            <CardCarousel
              restaurants={filteredRestaurants}
              currentLocation={currLocation}
              selectedRestaurantId={selected}
              onRestaurantSelect={handleRestaurantSelect}
              onScrollEnd={handleCarouselScrollEnd}
              onExpandCard={expandFromCarousel}
              onAdHocSearch={handleAdHocSearch}
              searchQuery={searchQuery}
            />
          )}
          renderHalf={() => {
            // If expanded from carousel, show ExpandedCard; otherwise show CompactList
            if (showExpandedCard) {
              const selectedRestaurant = filteredRestaurants.find(r => r._id === selected);
              return (
                <ExpandedCard
                  restaurant={selectedRestaurant}
                  currentLocation={currLocation}
                  onCollapse={collapseSheet}
                  variant="card"
                  searchQuery={searchQuery}
                />
              );
            }
            return (
              <CompactList
                restaurants={filteredRestaurants}
                currentLocation={currLocation}
                selectedRestaurantId={selected}
                onRestaurantSelect={handleRestaurantSelect}
                onAdHocSearch={handleAdHocSearch}
                searchQuery={searchQuery}
              />
            );
          }}
          renderFull={() => {
            // If showing expanded card, show it in full variant
            // Otherwise show full-height list
            if (showExpandedCard) {
              const selectedRestaurant = filteredRestaurants.find(r => r._id === selected);
              return (
                <ExpandedCard
                  restaurant={selectedRestaurant}
                  currentLocation={currLocation}
                  onCollapse={backToList}
                  variant="full"
                  searchQuery={searchQuery}
                />
              );
            }
            return (
              <CompactList
                restaurants={filteredRestaurants}
                currentLocation={currLocation}
                selectedRestaurantId={selected}
                onRestaurantSelect={handleRestaurantSelect}
                onAdHocSearch={handleAdHocSearch}
                searchQuery={searchQuery}
              />
            );
          }}
        />
      )}
      {/* Desktop/tablet toggle button - hide on mobile when using BottomSheet */}
      {!isMobile && (
        <RightContent
          className={`mobile-only ${view === LIST_VIEW ? "open" : "closed"}`}
        >
          {view === LIST_VIEW ? (
            <ClickableIconNoBorder
              onClick={() => toggleResults()}
              className="fas fa-map-marked-alt fa-2x"
            ></ClickableIconNoBorder>
          ) : (
            <ClickableIconNoBorder
              onClick={() => toggleResults()}
              className="fas fa-list fa-2x"
            ></ClickableIconNoBorder>
          )}
        </RightContent>
      )}
      {showFollowModal && followCityInfo && (
        <FollowCityModal
          cityName={followCityInfo.cityName}
          state={followCityInfo.state}
          lat={followCityInfo.lat}
          lng={followCityInfo.lng}
          onClose={() => setShowFollowModal(false)}
        />
      )}
      {showSaveModal && saveModalInfo && (
        <SaveSearchModal
          cityName={saveModalInfo.cityName}
          state={saveModalInfo.state}
          lat={saveModalInfo.lat}
          lng={saveModalInfo.lng}
          keywords={saveModalInfo.keywords}
          cuisine={saveModalInfo.cuisine}
          radius={saveModalInfo.radius}
          zoom={saveModalInfo.zoom}
          onClose={() => setShowSaveModal(false)}
        />
      )}
    </div>
  );
};
const mapStateToProps = (state) => ({
  currLocation: state.currentLocation,
  restaurants: state.restaurant.restaurants,
  selected: state.restaurant.selected,
  mapCenter: state.mapCenter,
  selectedCuisines: state.selectedCuisines,
  view: state.results.view,
  adHocSearchActive: state.restaurant.adHocSearchActive,
  adHocSearchJobId: state.restaurant.adHocSearchJobId,
  adHocRestaurantsProcessed: state.restaurant.adHocRestaurantsProcessed,
  adHocTotalRestaurants: state.restaurant.adHocTotalRestaurants,
  adHocVeganResultsFound: state.restaurant.adHocVeganResultsFound,
  minLeafCount: state.filters.minLeafCount,
  verificationTier: state.filters.verificationTier,
  token: state.auth.token,
});
export default connect(mapStateToProps, {
  updateMapCenter,
  updateLocation,
  fetchResultsAction: fetchResults,
  updateRadius,
  fetchLocation,
  toggleMap,
  toggleList,
  startAdHocSearchAction: startAdHocSearch,
  stopAdHocSearchAction: stopAdHocSearch,
  updateSelectedCuisinesAction: updateSelectedCuisines,
  selectRestaurantFromMapAction: selectRestaurantFromMap,
  setLocationDisplayAction: setLocationDisplay,
})(MapResults);
